import React, { useEffect, useState, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import BoldIcon from "../../../myfin_v2/assets/icons/BoldIcon.png";
import { ReactComponent as DelIcon } from "../../../myfin_v2/assets/icons/delete_red.svg";
import { ReactComponent as LinkIcon } from "../../../myfin_v2/assets/icons/Link.svg";
import FormulaIcon from "../../../myfin_v2/assets/icons/formulaIcon.png";
import linkedIcon from "../../../myfin_v2/assets/icons/linked.png";
import "./customTable.css";
import commoncss from "../../../myfin_v2/css/common.module.css";
import MenuIcon from "../../../myfin_v2/assets/icons/Menu.png";
import { ReactComponent as PlusIcon } from "../../../myfin_v2/assets/icons/plusprimary.svg";

const NewRowInputCell = ({ initialValue = "", placeholder = "Enter text", onCommit }) => {
  const [val, setVal] = useState(initialValue || "");
  const committedRef = useRef(false);

  const doCommit = (finalVal, triggerKey = "blur") => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(finalVal, triggerKey);
  };

  const handleChange = (e) => {
    setVal(e.target.value);
  };

  const handleBlur = (e) => {
    doCommit(e.target.value, "blur");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      if (e.key === "Tab") e.preventDefault();
      doCommit(e.target.value, e.key);
    }
  };

  return (
    <input
      type="text"
      value={val}
      placeholder={placeholder}
      autoFocus
      style={{
        border: "none",
        outline: "none",
        width: "100%",
        background: "transparent",
        color: "#205a46",
        fontStyle: "normal",
      }}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
};

const MERGE_KEYS = ["date", "particular", "PARTICULAR", "DATE"];

const genObjectId = () =>
  Array.from({ length: 24 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join("");

const isPrimitive = (v) =>
  v === null || ["string", "number", "boolean"].includes(typeof v);

const buildRowSpanMap = (rows, mergeKeys) => {
  const spanMap = {};
  let i = 0;

  while (i < rows.length) {
    let span = 1;

    for (
      let j = i + 1;
      j < rows.length && mergeKeys.every((k) => rows[j][k] === rows[i][k]);
      j++
    ) {
      span++;
    }

    mergeKeys.forEach((key) => {
      spanMap[`${i}-${key}`] = span;
      for (let k = i + 1; k < i + span; k++) {
        spanMap[`${k}-${key}`] = 0;
      }
    });

    i += span;
  }

  return spanMap;
};

const getStickyStyle = (
  col,
  isHeader,
  extraStyle = {},
  isPrePopulate = false,
  isLastSimpleCol = false,
  isFirstCol = false,
  prePopulateCashflowUI = false,
  tableType = "",
  isMainHeader = false
) => {
  const base = {};
  if (isPrePopulate && isHeader) {
    if (isMainHeader) {
      base.verticalAlign = "top";
      base.borderBottom = "1px solid #ccd5d3";
      if (isLastSimpleCol && tableType !== "cashflow") {
        base.borderRight = "1px solid #ccd5d3";
      }
    } else {
      base.verticalAlign = "middle";
      base.borderBottom = "1px solid #ccd5d3";
      if (isLastSimpleCol && tableType !== "cashflow") {
        base.borderRight = "1px solid #ccd5d3";
      }
    }
  }

  const colLower = String(col).trim().toLowerCase();

  if (isPrePopulate && tableType === "cashflow") {
    base.borderRight = "none";
  }

  if (colLower === "particular" || isFirstCol) {
    return {
      ...base,
      position: "sticky",
      left: 0,
      zIndex: isHeader ? 100 : 30,
      background: isHeader ? "#eaeeed" : "#fff",
      paddingLeft: prePopulateCashflowUI && !isHeader ? "28px" : "24px",
      ...extraStyle,
    };
  }
  if (colLower === "basecurrentyear") {
    return {
      ...base,
      position: "sticky",
      left: "180px",
      zIndex: isHeader ? 100 : 3,
      background: isHeader ? "#eaeeed" : "#fff",
      textAlign: "right",
      width: "170px",
      minWidth: "100px",
      ...extraStyle,
    };
  }
  return { ...base, ...extraStyle };
};

const formatCellNumber = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const num = Math.round(Number(val));
  if (isNaN(num)) return String(val);
  if (num < 0) {
    return `(${Math.abs(num).toLocaleString("en-IN")})`;
  }
  return num.toLocaleString("en-IN");
};

const displaySimpleValue = (row, col) => {
  const v = row[col];
  if (v != null && v !== "") return String(v);
  if (row.isNewRow) return "Enter text";
  return "";
};

const displayCompanyValue = (company, period, row) => {
  if (String(row?.lineType || "").toLowerCase() === "header") return "";
  const v = company[period];
  if (v !== null && v !== undefined && v !== "") {
    return formatCellNumber(v);
  }
  if (row.isNewRow) return "0";
  return "";
};

const ConsoleTable = ({
  tableData,
  tableTitle = null,
  type,
  id,
  isPrePopulate = false,
  allowDragAndAdd = false,
  prePopulateCashflowUI = false,
  onRowsChange = () => {},
  onRowAdd = () => {},
  onRowDelete = null,
  onCellLinkClick = () => {},
  linkedCells = {},
}) => {
  const cashflowUI =
    prePopulateCashflowUI || (allowDragAndAdd && isPrePopulate && type === "cashflow");
  const [localRows, setLocalRows] = useState(
    Array.isArray(tableData) ? [...tableData] : []
  );

  useEffect(() => {
    setLocalRows(Array.isArray(tableData) ? [...tableData] : []);
  }, [tableData]);

  const [dndReady, setDndReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setDndReady(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const [editing, setEditing] = useState(null);

  const startEdit = (rowIdx, field, initialVal) => {
    if (type !== "cashflow") return;
    setEditing({ rowIdx, field, val: initialVal });
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  const commitValue = () => {
    if (!editing) return;
    const { rowIdx, field, val } = editing;
    const row = localRows[rowIdx];
    if (!row) {
      setEditing(null);
      return;
    }

    const updated = [...localRows];
    const wasNew = row.isNewRow === true;

    if (field === "particular") {
      updated[rowIdx] = { ...row, isNewRow: false, particular: val };
    } else {
      let parsed = Number(String(val).replace(/,/g, ""));
      if (isNaN(parsed)) parsed = 0;

      const [cIdx, period] = field.split("-");
      const c = [...(row.companies || [])];
      if (c[cIdx]) {
        c[cIdx] = { ...c[cIdx], [period]: parsed };
      }
      updated[rowIdx] = { ...row, isNewRow: false, companies: c };
    }

    setLocalRows(updated);
    setEditing(null);

    if (wasNew && typeof onRowAdd === 'function' && field === "particular") {
        onRowAdd(rowIdx, updated[rowIdx]);
        return;
    }

    try {
      onRowsChange(updated, id);
    } catch (e) {
      console.error("onRowsChange error", e);
    }
  };

  if (!localRows?.length) return null;

  const isNumericColumn = (col) => {
    const colLower = String(col).trim().toLowerCase();
    if (colLower === "particular" || colLower === "date") return false;
    return localRows.some((row) => {
      const val = row[col];
      if (val === null || val === undefined || val === "") return false;
      return typeof val === "number" || (!isNaN(val) && !isNaN(parseFloat(val)));
    });
  };

  const simpleColumns = Object.keys(localRows[0]).filter(
    (key) =>
      !key.startsWith("_") &&
      key !== "companies" &&
      key !== "lineType" &&
      key !== "currentYear" &&
      key !== "previousYear" &&
      key !== "baseCurrentYear" &&
      key !== "basePreviousYear" &&
      key !== "isNewRow" &&
      key !== "isBold" &&
      isPrimitive(localRows[0][key])
  );

  const companies = localRows[0].companies || [];

  const periods = Array.from(
    new Set(
      companies.flatMap((company) =>
        Object.keys(company).filter((k) => k !== "title" && !k.startsWith("_"))
      )
    )
  );

  const rowSpanMap = buildRowSpanMap(localRows, MERGE_KEYS);
  const normalize = (str) =>
    String(str || "").replace(/[^a-z0-9]/gi, "").toLowerCase();

  const isNotesView =
    isPrePopulate && type && ["BS notes", "PL notes"].includes(type);
  const isNotesOrDisclosure =
    isPrePopulate &&
    type &&
    ["BS notes", "PL notes", "disclosure"].includes(type);
  const isCashflowScrollView =
    isPrePopulate && type === "cashflow" && cashflowUI;

  const droppableId = `console-table-rows-${id || type || "default"}`;

  const isHeaderRow = (row) =>
    (row?.lineType || "").toLowerCase() === "header";

  const mergeReorderedDataRows = (dataRows) => {
    let dataIdx = 0;
    return localRows.map((row) =>
      isHeaderRow(row) ? row : dataRows[dataIdx++]
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    
    let dataRowsCount = 0;
    let absoluteFromIndex = -1;
    for (let i = 0; i < localRows.length; i++) {
      if (!isHeaderRow(localRows[i])) {
        if (dataRowsCount === result.source.index) {
          absoluteFromIndex = i;
          break;
        }
        dataRowsCount++;
      }
    }

    const dataRows = localRows.filter((row) => !isHeaderRow(row));
    const items = Array.from(dataRows);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const merged = mergeReorderedDataRows(items);
    
    dataRowsCount = 0;
    let absoluteToIndex = -1;
    for (let i = 0; i < merged.length; i++) {
      if (!isHeaderRow(merged[i])) {
        if (dataRowsCount === result.destination.index) {
          absoluteToIndex = i;
          break;
        }
        dataRowsCount++;
      }
    }

    setLocalRows(merged);
    try {
      const fromIdx = absoluteFromIndex >= 0 ? absoluteFromIndex : undefined;
      const toIdx = absoluteToIndex >= 0 ? absoluteToIndex : undefined;
      onRowsChange(merged, null, fromIdx, toIdx);
    } catch (e) {
      console.error("onRowsChange error", e);
    }
  };

  const buildEmptyCompanies = () =>
    companies.map((company) => {
      const entry = { title: company.title };
      periods.forEach((p) => {
        entry[p] = "";
      });
      return entry;
    });

  const handleNewRowCommit = (rowIndex, finalVal, triggerKey) => {
    const row = localRows[rowIndex];
    if (!row || !row.isNewRow) return;
    const particularVal = finalVal?.trim() || "";
    const updated = [...localRows];
    const updatedRow = {
      ...row,
      isNewRow: false,
      particular: particularVal,
      lineType: row.lineType === "row" ? "line item" : (row.lineType || "line item"),
    };
    updated[rowIndex] = updatedRow;
    setLocalRows(updated);
    if (typeof onRowAdd === 'function') {
      onRowAdd(rowIndex, updatedRow);
    }
  };

  const addNewRow = (insertIdx) => {
    const newRow = {
      _id: genObjectId(),
      isNewRow: true,
      lineType: "line item",
      particular: "",
      formulaType: null,
      isBold: false,
      companies: buildEmptyCompanies(),
    };
    simpleColumns.forEach((col) => {
      newRow[col] = "";
    });
    const updated = [...localRows];
    updated.splice(insertIdx, 0, newRow);
    setLocalRows(updated);
  };

  const toggleBold = (rowIndex) => {
    const updated = [...localRows];
    updated[rowIndex] = { ...updated[rowIndex], isBold: !(updated[rowIndex].isBold === true || String(updated[rowIndex].isBold).toLowerCase() === "true") };
    setLocalRows(updated);
    if (typeof onRowsChange === "function") {
      onRowsChange(updated, id);
    }
  };

  const deleteRow = (rowIndex) => {
    const updated = [...localRows];
    updated.splice(rowIndex, 1);
    setLocalRows(updated);
    if (typeof onRowDelete === "function") {
      onRowDelete(rowIndex, updated);
    } else if (typeof onRowsChange === "function") {
      onRowsChange(updated, id);
    }
  };

  const totalCols =
    simpleColumns.length + companies.length * periods.length + (type === "cashflow" ? 1 : 0);

  const renderRowCells = (row, rowIndex, dragProvided = null, isDragging = false) => {
    const isHeader = isHeaderRow(row);
    const totalColumnsCount = simpleColumns.length + (companies.length * periods.length) + (type === "cashflow" ? 1 : 0);
    let cellIndexCounter = 0;

    return (
      <>
        {simpleColumns.map((col, colIdx) => {
          const colLower = String(col).trim().toLowerCase();
          const isFirstCol = colIdx === 0;
          const isStickyCol = colLower === "particular" || isFirstCol;
          const isNumeric = isNumericColumn(col);

          const tdClass = isStickyCol
            ? "col-text"
            : isNumeric
            ? "col-number"
            : "col-text-nonsticky";

          const alignStyle = {
            textAlign: isStickyCol ? "left" : isNumeric ? "right" : "left",
          };

          const isEmptyPlaceholder =
            row.isNewRow && (row[col] == null || row[col] === "");
          const cellText = displaySimpleValue(row, col);

          if (MERGE_KEYS.includes(col) || MERGE_KEYS.includes(colLower)) {
            const span = rowSpanMap[`${rowIndex}-${col}`];
            if (span === 0) return null;

            return (
              <td
                key={col}
                rowSpan={span}
                className={`${tdClass} ${isHeader ? "header-cell" : ""}`}
                style={{
                  ...getStickyStyle(
                    col,
                    isHeader,
                    alignStyle,
                    isPrePopulate,
                    false,
                    isFirstCol,
                    cashflowUI,
                    type
                  ),
                  backgroundColor: isDragging ? "#fff" : (isHeader ? undefined : undefined),
                  borderTop: isDragging ? "1px solid #3AB67D" : undefined,
                  borderBottom: isDragging ? "1px solid #3AB67D" : undefined,
                  borderLeft: (isDragging && cellIndexCounter === 0) ? "1px solid #3AB67D" : undefined,
                  borderRight: (isDragging && cellIndexCounter === totalColumnsCount - 1) ? "1px solid #3AB67D" : undefined,
                }}
              >
                {(() => { cellIndexCounter++; return null; })()}
                {cashflowUI && isFirstCol && dragProvided && (
                  <div
                    {...dragProvided.dragHandleProps}
                    className="ft-row-drag-handle"
                    style={{
                      position: "absolute",
                      left: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Drag to reorder"
                  >
                    <img src={MenuIcon} alt="drag" style={{ width: 9, height: 13 }} />
                  </div>
                )}
                {row.isNewRow && colLower === "particular" ? (
                  <NewRowInputCell
                    initialValue={row.particular || ""}
                    placeholder="Enter text"
                    onCommit={(finalVal, triggerKey) => handleNewRowCommit(rowIndex, finalVal, triggerKey)}
                  />
                ) : (
                  editing && editing.rowIdx === rowIndex && editing.field === "particular" ? (
                    <input
                      autoFocus
                      defaultValue={row.particular || ""}
                      style={{ border: "none", outline: "none", width: "100%", background: "transparent", color: "#205a46" }}
                      onChange={(e) => setEditing({ ...editing, val: e.target.value })}
                      onBlur={commitValue}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitValue();
                        if (e.key === "Escape") cancelEdit();
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        color: isEmptyPlaceholder ? "#aaa" : "inherit",
                        fontStyle: "normal",
                        cursor: type === "cashflow" ? "text" : "default"
                      }}
                      onClick={() => {
                        if (type === "cashflow") {
                          startEdit(rowIndex, "particular", row.particular || "");
                        }
                      }}
                    >
                      {cellText}
                    </span>
                  )
                )}
              </td>
            );
          }

          return (
            <td
              key={col}
              className={`${tdClass} ${isHeader ? "header-cell" : ""}`}
              style={{
                ...getStickyStyle(
                  col,
                  isHeader,
                  alignStyle,
                  isPrePopulate,
                  false,
                  isFirstCol,
                  cashflowUI,
                  type
                ),
                backgroundColor: isDragging ? "#fff" : undefined,
                borderTop: isDragging ? "1px solid #3AB67D" : undefined,
                borderBottom: isDragging ? "1px solid #3AB67D" : undefined,
                borderLeft: (isDragging && cellIndexCounter === 0) ? "1px solid #3AB67D" : undefined,
                borderRight: (isDragging && cellIndexCounter === totalColumnsCount - 1) ? "1px solid #3AB67D" : undefined,
              }}
            >
              {(() => { cellIndexCounter++; return null; })()}
              {cashflowUI && isFirstCol && dragProvided && (
                <div
                  {...dragProvided.dragHandleProps}
                  className="ft-row-drag-handle"
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title="Drag to reorder"
                >
                  <img src={MenuIcon} alt="drag" style={{ width: 9, height: 13 }} />
                </div>
              )}
              {row.isNewRow && colLower === "particular" ? (
                <NewRowInputCell
                  initialValue={row.particular || ""}
                  placeholder="Enter text"
                  onCommit={(finalVal, triggerKey) => handleNewRowCommit(rowIndex, finalVal, triggerKey)}
                />
              ) : (
                editing && editing.rowIdx === rowIndex && editing.field === "particular" ? (
                  <input
                    autoFocus
                    defaultValue={row.particular || ""}
                    style={{ border: "none", outline: "none", width: "100%", background: "transparent", color: "#205a46" }}
                    onChange={(e) => setEditing({ ...editing, val: e.target.value })}
                    onBlur={commitValue}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitValue();
                      if (e.key === "Escape") cancelEdit();
                    }}
                  />
                ) : (
                  <span
                    style={{
                      color: isEmptyPlaceholder ? "#aaa" : "inherit",
                      fontStyle: "normal",
                      cursor: type === "cashflow" ? "text" : "default"
                    }}
                    onClick={() => {
                      if (type === "cashflow") {
                        startEdit(rowIndex, "particular", row.particular || "");
                      }
                    }}
                  >
                    {cellText}
                  </span>
                )
              )}
            </td>
          );
        })}

        {(row.companies || []).map((company, cIdx) =>
          periods.map((period) => {
            const isEmptyPlaceholder =
              row.isNewRow &&
              (company[period] == null || company[period] === "");
            const cellText = displayCompanyValue(company, period, row);

            const isCyPyEditing = editing && editing.rowIdx === rowIndex && editing.field === `${cIdx}-${period}`;

            return (
              <td
                key={`${rowIndex}-${cIdx}-${period}`}
                className={`col-number ${isHeader ? "header-cell" : ""}`}
                style={{
                  position: "relative",
                  backgroundColor: isDragging ? "#fff" : undefined,
                  borderTop: isDragging ? "1px solid #3AB67D" : undefined,
                  borderBottom: isDragging ? "1px solid #3AB67D" : undefined,
                  borderLeft: (isDragging && cellIndexCounter === 0) ? "1px solid #3AB67D" : undefined,
                  borderRight: (isDragging && cellIndexCounter === totalColumnsCount - 1) ? "1px solid #3AB67D" : undefined,
                  paddingRight: type === "cashflow" && !isHeader ? "35px" : undefined,
                }}
                onClick={(e) => {
                  if (type === "cashflow") {
                    e.stopPropagation();
                    startEdit(rowIndex, `${cIdx}-${period}`, company[period] || "");
                  }
                }}
              >
                {(() => { cellIndexCounter++; return null; })()}
                {isCyPyEditing ? (
                  <input
                    autoFocus
                    defaultValue={company[period] || ""}
                    style={{ border: "none", outline: "none", width: "100%", textAlign: "right", background: "transparent", color: "#205a46" }}
                    onChange={(e) => setEditing({ ...editing, val: e.target.value })}
                    onBlur={commitValue}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitValue();
                      if (e.key === "Escape") cancelEdit();
                    }}
                  />
                ) : (
                  <>
                    <span
                      style={{
                        color: isEmptyPlaceholder ? "#aaa" : "inherit",
                        fontStyle: "normal",
                        cursor: type === "cashflow" ? "text" : "default"
                      }}
                    >
                      {cellText}
                    </span>
                    {type === "cashflow" && !isHeader && (() => {
                      const cellFormula = (Array.isArray(row.cellFormulas) ? row.cellFormulas : []).find(
                        (f) => String(f.col).toLowerCase() === String(period).toLowerCase()
                      ) || (linkedCells && (linkedCells[`${id}-${rowIndex}-${period}`] || linkedCells[`${id}-${rowIndex}-${period.toLowerCase()}`]));

                      const hasFormula =
                        row.type === "formula" ||
                        !!row.formula ||
                        cellFormula?.type === "formula";

                      const hasLink =
                        !!row.type ||
                        !!cellFormula;

                      const isHighlighted = hasFormula || hasLink;

                      return (
                        <span
                          className="consol-cashflow-link-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCellLinkClick(row, period, id, isHighlighted);
                          }}
                          style={{
                            display: isHighlighted ? "flex" : undefined,
                            background: isHighlighted ? "transparent" : undefined,
                          }}
                        >
                          {hasFormula ? (
                            <img src={FormulaIcon} style={{ width: 16, height: 16 }} alt="formula" />
                          ) : hasLink ? (
                            <img src={linkedIcon} style={{ width: 20, height: 10 }} alt="link" />
                          ) : (
                            <LinkIcon style={{ width: 16, height: 16, color: "#fff" }} />
                          )}
                        </span>
                      );
                    })()}
                  </>
                )}
              </td>
            );
          })
        )}

        {/* Bold and Delete Icons for the last column in cashflow */}
        {type === "cashflow" && (
          <td
            className={`col-number ${isHeader ? "header-cell" : ""}`}
            style={{
              width: "60px",
              position: "relative",
              borderTop: isDragging ? "1px solid #3AB67D" : undefined,
              borderBottom: isDragging ? "1px solid #3AB67D" : undefined,
              borderRight: isDragging ? "1px solid #3AB67D" : undefined,
            }}
          >
            <div style={{ display: "flex", gap: "8px", justifyContent: "center", alignItems: "center" }}>
              <div
                className={`consol-row-bold-icon ${(row.isBold === true || String(row.isBold).toLowerCase() === "true") ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleBold(rowIndex);
                }}
                title={(row.isBold === true || String(row.isBold).toLowerCase() === "true") ? "Remove Bold" : "Make Bold"}
              >
                <img src={BoldIcon} alt="Bold" style={{ width: 14, height: 14 }} />
              </div>
              <div
                className="consol-row-delete-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteRow(rowIndex);
                }}
                title="Delete row"
              >
                <DelIcon style={{ width: 16, height: 16 }} />
              </div>
            </div>
          </td>
        )}
      </>
    );
  };

  const renderBodyRows = () => {
    if (!cashflowUI || !dndReady) {
      return localRows.map((row, rowIndex) => {
        const rowId = normalize(row.particular);
        return (
          <tr
            key={row._id || rowIndex}
            id={rowId}
            className={isHeaderRow(row) ? "row-header" : ""}
            style={{ fontWeight: (row.isBold === true || String(row.isBold).toLowerCase() === "true") ? 700 : undefined }}
          >
            {renderRowCells(row, rowIndex)}
          </tr>
        );
      });
    }

    let dragIndex = 0;
    return localRows.map((row, rowIndex) => {
      const rowKey = row._id ?? rowIndex;
      const rowId = normalize(row.particular);

      if (isHeaderRow(row)) {
        return (
          <React.Fragment key={rowKey}>
            {rowIndex > 0 && cashflowUI && (
              <tr key={`add-line-${rowIndex}`}>
                <td colSpan={totalCols} style={{ border: "none", padding: "4px 0" }}>
                  <button
                    onClick={() => addNewRow(rowIndex)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#205a46",
                      cursor: "pointer",
                      fontWeight: "500",
                      padding: "8px 12px 8px 20px",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      position: "sticky",
                      left: 0,
                    }}
                  >
                    <PlusIcon style={{ width: 14, height: 14, marginTop: "-2px" }} /> Add Line Item
                  </button>
                </td>
              </tr>
            )}
            <tr id={rowId} className="row-header" style={{ fontWeight: (row.isBold === true || String(row.isBold).toLowerCase() === "true") ? 700 : undefined }}>
              {renderRowCells(row, rowIndex)}
            </tr>
          </React.Fragment>
        );
      }

      const draggableId = `${droppableId}-${rowKey}`;
      const currentDragIndex = dragIndex++;

      return (
        <Draggable
          key={draggableId}
          draggableId={draggableId}
          index={currentDragIndex}
        >
          {(dragProvided, snapshot) => {
            const isDragging = snapshot.isDragging;
            const draggingStyle = isDragging ? {
              backgroundColor: "#fff",
              boxShadow: "0px 10px 20px rgba(0,0,0,0.15)",
              zIndex: 9999,
              display: "table"
            } : {};
            return (
            <tr
              ref={dragProvided.innerRef}
              {...dragProvided.draggableProps}
              id={rowId}
              style={{ fontWeight: (row.isBold === true || String(row.isBold).toLowerCase() === "true") ? 700 : undefined, ...dragProvided.draggableProps.style, ...draggingStyle }}
            >
              {renderRowCells(row, rowIndex, dragProvided, isDragging)}
            </tr>
          )}}
        </Draggable>
      );
    });
  };

  const tableElement = (
    <table
      className="financial-table"
      style={{
        width: "100%",
        minWidth: "100%",
        borderCollapse: "separate",
      }}
    >
      <thead
        style={
          isPrePopulate
            ? { position: "sticky", top: 0, zIndex: 100, backgroundColor: "#eaeeed" }
            : undefined
        }
      >
        <tr>
          {simpleColumns.map((col, idx) => {
            const colLower = String(col).trim().toLowerCase();
            const isFirstCol = idx === 0;
            const isStickyCol = colLower === "particular" || isFirstCol;
            const isNumeric = isNumericColumn(col);

            const thClass = isStickyCol
              ? "col-text particular-heading"
              : isNumeric
              ? "col-number"
              : "col-text-nonsticky";

            const alignStyle = {
              textAlign: isStickyCol ? "left" : isNumeric ? "right" : "left",
            };

            return (
              <th
                key={col}
                rowSpan={isPrePopulate ? 1 : 2}
                className={`${thClass} tableHeadingColor`}
                style={getStickyStyle(
                  col,
                  true,
                  {
                    ...alignStyle,
                  },
                  isPrePopulate,
                  isPrePopulate && idx === simpleColumns.length - 1,
                  isFirstCol,
                  false,
                  type,
                  true
                )}
              >
                {col.toUpperCase()}
              </th>
            );
          })}

          {companies.map((company, idx) => (
            <th
              key={idx}
              className="col-group tableHeadingColor"
              colSpan={periods.length}
            >
              {company.title}
            </th>
          ))}
          {type === "cashflow" && (
            <th className="col-group tableHeadingColor" style={{ width: "60px" }}></th>
          )}
        </tr>

        <tr>
          {isPrePopulate && simpleColumns.map((col, idx) => {
            const isFirstCol = idx === 0;
            const isStickyCol = String(col).trim().toLowerCase() === "particular" || isFirstCol;
            return (
              <th
                key={`empty-${col}`}
                className={`${isStickyCol ? "col-text particular-heading" : "col-text-nonsticky"} tableHeadingColor`}
                style={getStickyStyle(
                  col,
                  true,
                  {},
                  isPrePopulate,
                  isPrePopulate && idx === simpleColumns.length - 1,
                  isFirstCol,
                  cashflowUI,
                  type,
                  false
                )}
              ></th>
            );
          })}
          {companies.map(() =>
            periods.map((p) => (
              <th
                key={p}
                className="col-number tableHeadingColor"
                style={cashflowUI && String(p).trim().toUpperCase() === "PREVIOUSYEAR" ? { borderRight: "none" } : undefined}
              >
                {p}
              </th>
            ))
          )}
          {type === "cashflow" && (
            <th className="col-number tableHeadingColor" style={{ width: "60px" }}></th>
          )}
        </tr>
      </thead>

      {cashflowUI && dndReady ? (
        <Droppable droppableId={droppableId} type="ROW">
          {(provided) => (
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              {renderBodyRows()}
              {provided.placeholder}
            </tbody>
          )}
        </Droppable>
      ) : (
        <tbody>{renderBodyRows()}</tbody>
      )}

      {cashflowUI && (
        <tfoot>
          <tr>
            <td colSpan={totalCols} style={{ border: "none", padding: "4px 0" }}>
              <button
                type="button"
                onClick={() => addNewRow(localRows.length)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#205a46",
                  cursor: "pointer",
                  fontWeight: "500",
                  padding: "8px 12px 8px 20px",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  position: "sticky",
                  left: 0,
                }}
              >
                <PlusIcon style={{ width: 14, height: 14 }} /> Add Line Item
              </button>
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );

  return (
    <>
      <div
        className="table-outer"
        style={{
          width: "100%",
          marginTop: isNotesOrDisclosure
            ? ["BS notes", "PL notes"].includes(type)
              ? "5px"
              : "25px"
            : "0px",
          marginBottom: type === "disclosure" ? "25px" : "0px",
        }}
        id={id}
      >
        {tableTitle && (
          <div
            className="table-title-wrapper"
            style={
              isNotesView ? { borderRadius: "10px 10px 0 0" } : undefined
            }
          >
            <span
              className={`table-title ${commoncss.fs22} ${commoncss.fw500}`}
            >
              {tableTitle}
            </span>
          </div>
        )}
        <div
          className="table-container1"
          style={{
            width: "100%",
            border: "1px solid #ccd5d3",
            borderRadius: "10px",
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: isPrePopulate
              ? isNotesView
                ? "calc(100vh - 95px)"
                : type === "cashflow"
                ? "calc(100vh - 95px)"
                : "calc(100vh - 95px)"
              : "calc(88vh - 60px)",
            borderTop: tableTitle ? "none" : "1px solid #ccd5d3",
            borderTopLeftRadius: tableTitle ? "0" : "10px",
            borderTopRightRadius: tableTitle ? "0" : "10px",
          }}
        >
          {cashflowUI ? (
            <DragDropContext onDragEnd={onDragEnd}>{tableElement}</DragDropContext>
          ) : (
            tableElement
          )}
        </div>
      </div>
    </>
  );
};

export default ConsoleTable;
